import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useService } from '../contexts/ServiceContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useZnoospaceProgram } from '../hooks/useChirpXProgram';
import { useZnoozinuToken } from '../hooks/useZnoozinuToken';
import { useDebounceValue } from '../hooks/useDebounce';
import { extractHashtags, parseTagsAndMentions, searchPosts } from '../utils/hashtagUtils';
import { formatAddressAsUsername, formatAddressForDisplay } from '../utils/addressUtils';
import { PublicKey } from '@solana/web3.js';
import WelcomeMessage from './WelcomeMessage';
import SearchComponent from './SearchComponent';
import ContentFlagging from './ContentFlagging';
import StakeModal from './StakeModal';
import MediaUpload from './MediaUpload';
import { ReputationService } from '../services/ReputationService';
import serviceFactory from '../services/ServiceFactory';
import { 
  sanitizePostContent, 
  checkRateLimit, 
  sanitizeErrorMessage 
} from '../utils/security';
import { addCurrentWalletTestProfile, listAllProfilesInIndex } from '../utils/profileIndexHelper';
import { getBestAvatar, generateGradientFromSeed } from '../utils/avatarUtils';
import { secureStorage } from '../utils/security';
import DynamicGovernanceConfig from '../services/DynamicGovernanceConfig';
import AdminPanel from './AdminPanel';

import './MainContent.css';

const MainContent = ({ walletAddress: propWalletAddress }) => {
  // Use onboarding context for profile management
  const { 
    profile, 
    isCompleted, 
    hasProfile, 
    isWelcoming, 
    showWelcome, 
    welcomeDisplayName, 
    dismissWelcome,
    address: onboardingWalletAddress  // OnboardingContext exports 'address', not 'walletAddress'
  } = useOnboarding();

  // Use wallet address from onboarding context (more reliable)
  const walletAddress = onboardingWalletAddress || propWalletAddress;
  
  // State for complete profile data (including profileImage)
  const [completeProfile, setCompleteProfile] = useState(null);
  const [detectedProfileImage, setDetectedProfileImage] = useState(null);
  
  // Load complete profile from secure storage for consistency with ProfilePage
  useEffect(() => {
    const loadCompleteProfile = async () => {
      if (walletAddress) {
        try {
          // Loading profile for wallet
          
          // Check secure storage first (same as ProfilePage)
          const savedProfile = await secureStorage.getItem(`profile_${walletAddress}`);
          // Check secure storage result
          
          if (savedProfile) {
            const parsedProfile = JSON.parse(savedProfile);
            // Profile parsed from storage
            
            // Use the exact same structure as ProfilePage
            const normalizedProfile = {
              displayName: parsedProfile.displayName || profile?.displayName || 'Anonymous User',
              username: parsedProfile.username || profile?.username || `@${walletAddress?.slice(0, 8)}...`,
              bio: parsedProfile.bio || 'Most people overestimate what they can do awake in a year, and underestimate what they can dream in a decade. 🏔️🎁 Dream bigger. Snore louder. ZNOOZINU 🏔️🔊',
              location: parsedProfile.location || 'San Francisco',
              website: parsedProfile.website || '',
              bannerImage: parsedProfile.bannerImage || '',
              profileImage: parsedProfile.profileImage || '', // This should contain the base64 image
              joinDate: parsedProfile.joinDate || 'May 2025',
              followingCount: parsedProfile.followingCount || 0,
              followersCount: parsedProfile.followersCount || 0,
              postsCount: parsedProfile.postsCount || 38
            };
            
            // Profile normalized and ready
            
            setCompleteProfile(normalizedProfile);
          } else {
            // No saved profile, checking onboarding context
            
            // Use onboarding profile if no saved profile
            if (profile) {
              // Using onboarding context profile
              setCompleteProfile(profile);
            }
          }
        } catch (error) {
          console.error('🔍 PROFILE DEBUG: Error loading profile:', error);
        }
      }
    };
    
    loadCompleteProfile();
  }, [walletAddress, profile]);
  
  // Profile image is now loaded from secure storage above - no DOM sniffing needed
  
  // Debug logging for wallet address
  // MainContent wallet address validation

  const {
    posts,
    loading,
    error,
    isConnected,
    publicKey,
    createUserProfile,
    createPost: createBlockchainPost,
    likePost: likeBlockchainPost,
    solanaService,
  } = useZnoospaceProgram(walletAddress);

  // ZNOOZINU token integration for premium features
  const {
    isPremium,
    balance: tokenBalance,
    loading: tokenStatusLoading,
    hasFeature,
    getCharacterLimit,
    getMaxImages,
    refreshTokenStatus
  } = useZnoozinuToken(walletAddress);

  const [beams, setBeams] = useState([]);
  const [beamsLoading, setBeamsLoading] = useState(false);
  const [newBeamContent, setNewBeamContent] = useState('');
  const [newBeamMedia, setNewBeamMedia] = useState('');
  const [showCreateBeam, setShowCreateBeam] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreBeams, setHasMoreBeams] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [totalBeams, setTotalBeams] = useState(0);
  const [showPremiumInfo, setShowPremiumInfo] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [showSearch, setShowSearch] = useState(false);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [showFlagging, setShowFlagging] = useState(false);
  const [flaggingContentId, setFlaggingContentId] = useState(null);
  const [reputationService, setReputationService] = useState(null);
  const [showStakeModal, setShowStakeModal] = useState(false);
  const [requiredStake, setRequiredStake] = useState(0);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [activeTab, setActiveTab] = useState('foryou');
  const [followVersion, setFollowVersion] = useState(0);

  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [followingBeams, setFollowingBeams] = useState([]);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [forYouBeams, setForYouBeams] = useState([]);
  const [forYouLoading, setForYouLoading] = useState(false);
  const [forYouCursor, setForYouCursor] = useState(null);
  const [forYouHasMore, setForYouHasMore] = useState(false);
  const [blockVersion, setBlockVersion] = useState(0);
  const [showBlockedUsers, setShowBlockedUsers] = useState(false);
  const [postMenuOpen, setPostMenuOpen] = useState(null);
  const [profileBeams, setProfileBeams] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);

  // Progressive rendering: only render a batch of beams at a time to avoid DOM bloat
  const RENDER_BATCH_SIZE = 20;
  const [renderCount, setRenderCount] = useState(RENDER_BATCH_SIZE);
  const renderSentinelRef = useRef(null);

  // Reset render count when beams change (new search, tab switch, refresh)
  useEffect(() => {
    setRenderCount(RENDER_BATCH_SIZE);
  }, [beams, activeTab, searchQuery]);

  // IntersectionObserver to progressively render more beams as user scrolls
  useEffect(() => {
    const sentinel = renderSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setRenderCount(prev => prev + RENDER_BATCH_SIZE);
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [renderSentinelRef.current]);

  // Debounce search query for efficient filtering (300ms delay)
  const debouncedSearchQuery = useDebounceValue(searchQuery, 300);

  // Memoize filtered beams based on debounced search and active tab
  const displayBeams = useMemo(() => {
    // Get blocked users list
    const blockedUsers = solanaService?.getBlockedUsers ? solanaService.getBlockedUsers() : [];
    const blockedSet = new Set(blockedUsers);
    
    let filtered;
    
    if (activeTab === 'foryou') {
      // For You: global feed from all users (forYouBeams), fall back to own beams
      filtered = forYouBeams.length > 0 ? forYouBeams : beams;
    } else if (activeTab === 'following') {
      // Following: posts from followed users + own posts
      if (followingBeams.length > 0) {
        const seen = new Set(followingBeams.map(b => b.id || b.signature));
        const ownBeams = beams.filter(b => b.author === walletAddress && !seen.has(b.id || b.signature));
        filtered = [...followingBeams, ...ownBeams].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      } else {
        filtered = beams.filter(b => b.author === walletAddress);
      }
    } else if (activeTab === 'profile') {
      // Profile: only the current user's own posts
      filtered = profileBeams.length > 0 ? profileBeams : beams.filter(b => b.author === walletAddress);
    } else {
      filtered = beams;
    }
    
    // Filter out blocked users (never filter own posts)
    filtered = filtered.filter(b => !blockedSet.has(b.author) || b.author === walletAddress);
    
    if (!debouncedSearchQuery) return filtered;
    return searchPosts(filtered.map(b => ({ account: b })), debouncedSearchQuery, searchType)
      .map(result => result.account || result);
  }, [beams, forYouBeams, followingBeams, profileBeams, walletAddress, debouncedSearchQuery, searchType, activeTab, solanaService, followVersion, blockVersion]);

  // Top 3 followed users with their latest post snippet (for Following tab)
  const topFollowedUsers = useMemo(() => {
    if (!solanaService?.getFollowing) return [];
    const followedAddresses = solanaService.getFollowing();
    if (!followedAddresses || followedAddresses.length === 0) return [];

    // Use followingBeams if available, otherwise fall back to main beams
    const allBeams = followingBeams.length > 0 ? followingBeams : beams;

    // Group beams by followed author, pick latest per author
    const authorLatest = {};
    allBeams.forEach(beam => {
      if (followedAddresses.includes(beam.author)) {
        if (!authorLatest[beam.author] || beam.timestamp > authorLatest[beam.author].timestamp) {
          authorLatest[beam.author] = beam;
        }
      }
    });

    // Sort by most recent post, take top 3
    return Object.values(authorLatest)
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
      .slice(0, 3);
  }, [beams, followingBeams, solanaService, followVersion]);

  // Load beams on component mount
  useEffect(() => {
    if (walletAddress && solanaService) {
      refreshBeams();
    }
  }, [walletAddress]); // Removed solanaService from dependency array to prevent hoisting error

  // Load following feed when Following tab is active
  useEffect(() => {
    if (activeTab === 'following' && solanaService?.getFollowingFeed) {
      const loadFollowingFeed = async () => {
        setFollowingLoading(true);
        try {
          const result = await solanaService.getFollowingFeed(30);
          if (result.success && result.beams) {
            console.log(`👥 Following feed loaded: ${result.beams.length} beams`);
            setFollowingBeams(result.beams);
          }
        } catch (err) {
          console.error('❌ Failed to load following feed:', err);
        } finally {
          setFollowingLoading(false);
        }
      };
      loadFollowingFeed();
    }
  }, [activeTab, solanaService, followVersion]);

  // Load For You global feed when tab is active
  useEffect(() => {
    if (activeTab === 'foryou' && solanaService?.getForYouFeed) {
      const loadForYouFeed = async () => {
        setForYouLoading(true);
        try {
          const result = await solanaService.getForYouFeed(30);
          if (result.success && result.beams) {
            console.log(`🌐 For You feed loaded: ${result.beams.length} beams`);
            setForYouBeams(prev => {
              // Preserve optimistic beams not yet indexed
              const freshSigs = new Set(result.beams.map(b => b.id || b.signature));
              const surviving = prev.filter(b => b.isOptimistic && !freshSigs.has(b.id || b.signature));
              return surviving.length > 0 ? [...surviving, ...result.beams] : result.beams;
            });
            setForYouHasMore(result.hasMore || false);
            setForYouCursor(result.nextCursor || null);
          }
        } catch (err) {
          console.error('❌ Failed to load For You feed:', err);
        } finally {
          setForYouLoading(false);
        }
      };
      loadForYouFeed();
    }
  }, [activeTab, solanaService, blockVersion]);

  // Load Profile tab — user's own posts from blockchain
  useEffect(() => {
    if (activeTab === 'profile' && solanaService?.getUserPosts && walletAddress) {
      const loadProfilePosts = async () => {
        setProfileLoading(true);
        try {
          const userPublicKey = new PublicKey(walletAddress);
          const result = await solanaService.getUserPosts(userPublicKey);
          if (result && result.success && result.posts) {
            console.log(`👤 Profile: loaded ${result.posts.length} own posts`);
            setProfileBeams(result.posts);
          } else {
            setProfileBeams([]);
          }
        } catch (err) {
          console.error('❌ Failed to load profile posts:', err);
          // Fallback: filter own posts from main beams
          setProfileBeams(beams.filter(b => b.author === walletAddress));
        } finally {
          setProfileLoading(false);
        }
      };
      loadProfilePosts();
    }
  }, [activeTab, solanaService, walletAddress]);

  // Listen for block/unblock events to refresh feed
  useEffect(() => {
    const handleBlockChange = () => setBlockVersion(v => v + 1);
    window.addEventListener('znoospace_block_change', handleBlockChange);
    return () => window.removeEventListener('znoospace_block_change', handleBlockChange);
  }, []);

  // Listen for follow/unfollow events
  useEffect(() => {
    const handleFollowChange = () => setFollowVersion(v => v + 1);
    window.addEventListener('znoospace_follow_change', handleFollowChange);
    return () => window.removeEventListener('znoospace_follow_change', handleFollowChange);
  }, []);

  // Close post menu when clicking outside
  useEffect(() => {
    if (!postMenuOpen) return;
    const handleClickOutside = () => setPostMenuOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [postMenuOpen]);

  // Initialize ReputationService when wallet connects
  useEffect(() => {
    if (walletAddress && solanaService) {
      try {
        const repService = new ReputationService(solanaService.connection, {
          publicKey: new PublicKey(walletAddress)
        });
        setReputationService(repService);
        // ReputationService initialized
      } catch (error) {
        console.error('❌ Failed to initialize ReputationService:', error);
      }
    } else {
      setReputationService(null);
    }
  }, [walletAddress]); // Removed solanaService from dependency array to prevent hoisting error

  // Auto-load beams when wallet connects and onboarding completes
  useEffect(() => {
    if (walletAddress && isCompleted && solanaService) {
      // Auto-loading user posts after wallet connection
      loadBeamsFromBlockchain(1, false); // Load first page of user's posts
    }
  }, [walletAddress, isCompleted]); // Removed solanaService from dependency array to prevent hoisting error

  // Handle manual "Load my beams" button click
  const handleLoadMyBeams = async () => {
    if (!walletAddress || !solanaService) {
      // Cannot load beams: wallet not connected
      return;
    }

    // Manual beam loading requested
    try {
      // Reset pagination and load fresh posts from blockchain
      setCurrentPage(1);
      setHasMoreBeams(true);
      setNextCursor(null);
      await loadBeamsFromBlockchain(1, false);
      
      // Show success message
      // Successfully reloaded beams
    } catch (error) {
      console.error('❌ Error during manual beam loading:', error);
    }
  };

  // Handle search results
  const handleSearchResults = (results, query, type) => {
    setFilteredPosts(results || []);
    setSearchQuery(query);
    setSearchType(type);
  };

  // Handle content flagging
  const handleFlagContent = (contentId) => {
    if (!walletAddress) {
      // Cannot flag content - wallet not connected
      return;
    }
    // Flagging content
    setFlaggingContentId(contentId);
    setShowFlagging(true);
  };

  // Handle flag submission
  const handleFlagSubmitted = (result) => {
    // Flag submitted successfully
    setShowFlagging(false);
    setFlaggingContentId(null);
    // Could show success message or refresh content
  };

  // Handle close flagging modal
  const handleCloseFlagging = () => {
    setShowFlagging(false);
    setFlaggingContentId(null);
  };

  // Handle stake modal close
  const handleCloseStakeModal = () => {
    setShowStakeModal(false);
    setRequiredStake(0);
  };

  // Handle get tokens action
  const handleGetTokens = () => {
    // Open external link to get ZNOOZINU tokens
    window.open('https://jup.ag/swap/SOL-5cLKdANQXzUPMXgpba19Zwprhmi8yQW6R5D66oTQZ9ZN', '_blank');
    handleCloseStakeModal();
  };

  // Generate mock transaction ID for demonstration
  const generateMockTxId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 88; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Load beams from blockchain with pagination
  const loadBeamsFromBlockchain = async (page = 1, append = false) => {
    if (!solanaService) {
      // SolanaService not available
      return;
    }

    setBeamsLoading(true);
    try {
      // Loading beams page
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Blockchain operation timed out')), 30000)
      );
      
      // Callback for background refresh — updates React state with fresh data
      // Merges with optimistic beams so newly-posted content (with media) isn't lost
      const onRefresh = (freshResult) => {
        if (freshResult && freshResult.beams) {
          setBeams(prev => {
            const freshSigs = new Set(freshResult.beams.map(b => b.id || b.signature));
            // Keep optimistic beams that haven't been indexed by Helius yet
            const surviving = prev.filter(b => b.isOptimistic && !freshSigs.has(b.id || b.signature));
            if (surviving.length > 0) {
              console.log(`🔄 Background refresh: keeping ${surviving.length} optimistic beam(s) not yet indexed`);
              return [...surviving, ...freshResult.beams];
            }
            return freshResult.beams;
          });
          setTotalBeams(freshResult.totalBeams || freshResult.beams.length);
          setHasMoreBeams(freshResult.hasMore || false);
          if (freshResult.nextCursor) setNextCursor(freshResult.nextCursor);
        }
      };

      const result = await Promise.race([
        solanaService.getBeams(page, 20, append ? nextCursor : null, page === 1 && !append ? onRefresh : null),
        timeoutPromise
      ]);
      
      if (result && result.beams) {
        if (append) {
          setBeams(prevBeams => {
            try {
              // Deduplicate by signature to prevent duplicate key warnings
              const existingKeys = new Set(prevBeams.map(b => b.id || b.signature));
              const newBeams = result.beams.filter(b => !existingKeys.has(b.id || b.signature));
              return [...prevBeams, ...newBeams];
            } catch (stateError) {
              console.error('Error updating beams state:', stateError);
              return prevBeams;
            }
          });
        } else {
          try {
            // Merge: preserve optimistic beams not yet indexed by Helius
            setBeams(prev => {
              const freshSigs = new Set(result.beams.map(b => b.id || b.signature));
              const surviving = prev.filter(b => b.isOptimistic && !freshSigs.has(b.id || b.signature));
              return surviving.length > 0 ? [...surviving, ...result.beams] : result.beams;
            });
          } catch (stateError) {
            console.error('Error setting beams state:', stateError);
            return;
          }
        }
        
        setTotalBeams(result.totalBeams || result.beams.length);
        setHasMoreBeams(result.hasMore || false);
        setCurrentPage(page);
        
        // Store the pagination cursor for the next "Load More" call
        if (result.nextCursor) {
          setNextCursor(result.nextCursor);
        }
        
        // Beams loaded successfully
      }
    } catch (error) {
      console.error('Error loading beams:', error);
    } finally {
      setBeamsLoading(false);
    }
  };

  // Load more beams (pagination)
  const loadMoreBeams = () => {
    if (!beamsLoading && hasMoreBeams) {
      loadBeamsFromBlockchain(currentPage + 1, true);
    }
  };

  // Refresh beams (clear cache and reload)
  const refreshBeams = () => {
    setCurrentPage(1);
    setNextCursor(null);
    setSearchQuery('');
    loadBeamsFromBlockchain(1, false);
  };

  // Handle media upload completion
  const handleMediaUploaded = (uploadResults) => {
    setUploadedMedia(uploadResults);
    
    // Auto-populate media URL in the beam content if it's the first media
    if (uploadResults.length > 0 && !newBeamMedia) {
      setNewBeamMedia(uploadResults[0].url);
    }
    setMediaUploading(false);
  };

  const handleMediaUploadStart = () => {
    setMediaUploading(true);
  };

  const handleMediaUploadError = (error) => {
    console.error('❌ Media upload failed:', error);
    alert(`Media upload failed: ${error}`);
    setMediaUploading(false);
  };

  const clearUploadedMedia = () => {
    setUploadedMedia([]);
    setNewBeamMedia('');
  };

  // Create new beam with security validation
  // Text goes to Solana memo, media URL (IPFS) is included in memo data
  const createBeam = async (e) => {
    e.preventDefault();
    
    if (!newBeamContent.trim() && uploadedMedia.length === 0) {
      return;
    }

    // Rate limiting check
    if (walletAddress) {
      const rateLimitResult = checkRateLimit('POST_CREATION', walletAddress);
      if (!rateLimitResult.allowed) {
        console.error('Rate limit exceeded. Remaining requests:', rateLimitResult.remaining);
        alert(`Rate limit exceeded. Please wait before posting again. Remaining requests: ${rateLimitResult.remaining}`);
        return;
      }
    }

    // Sanitize post content (allow empty text if media is attached)
    const sanitizedContent = sanitizePostContent(newBeamContent);
    if (!sanitizedContent.trim() && uploadedMedia.length === 0) {
      console.error('Post content failed sanitization');
      alert('Post content contains invalid characters or is empty after sanitization.');
      return;
    }

    // Check if media is still uploading
    if (mediaUploading) {
      alert('Please wait for media upload to complete before posting.');
      return;
    }

    // Temporarily disable DAO stake requirements to prevent RPC rate limiting
    // TODO: Re-enable once RPC caching is properly implemented
    // Skipping stake requirements check
    
    setBeamsLoading(true);
    
    try {
      // Use IPFS URL from Pinata if media was uploaded
      let finalMediaUrl = newBeamMedia;
      if (uploadedMedia.length > 0) {
        finalMediaUrl = uploadedMedia[0].url;
      }

      // All posts go to Solana memo — media URL is stored in the memo JSON
      const postContent = sanitizedContent.trim() || (finalMediaUrl ? '📷' : '');
      let txSignature = null;
      if (solanaService) {
        const result = await solanaService.createPost(
          postContent,
          finalMediaUrl
        );
        
        if (!result.success) {
          console.error('❌ Failed to create beam:', result.error);
        } else {
          txSignature = result.signature || result.txId || null;
        }
      } else {
        // SolanaService not available, using fallback
        txSignature = generateMockTxId();
      }

      // Optimistic beam insertion — show immediately without waiting for Helius re-index
      const optimisticBeam = {
        id: txSignature || `optimistic_${Date.now()}`,
        content: postContent,
        author: walletAddress,
        username: profile?.username || walletAddress?.substring(0, 8),
        displayName: profile?.displayName || profile?.username || walletAddress?.substring(0, 8),
        timestamp: Math.floor(Date.now() / 1000),
        mediaUrl: finalMediaUrl || null,
        mediaType: uploadedMedia.length > 0 ? uploadedMedia[0].mediaType : null,
        likes: 0,
        reposts: 0,
        isOptimistic: true,
      };
      setBeams(prev => [optimisticBeam, ...prev]);

      // Reset form
      setNewBeamContent('');
      setNewBeamMedia('');
      setUploadedMedia([]);
      setShowMediaUpload(false);

      // Clear stale beam cache so the next load doesn't overwrite the optimistic beam
      localStorage.removeItem('znoospace_beam_cache');
      
      // Background refresh to get the real blockchain data
      // Merge with optimistic beam so it persists until Helius indexes the new post
      setTimeout(() => {
        loadBeamsFromBlockchain(1, false);
      }, 10000);
      
    } catch (error) {
      console.error('Error creating beam:', error);
      alert(`Failed to create beam: ${error.message}`);
    } finally {
      setBeamsLoading(false);
      setMediaUploading(false);
    }
  };

  // Like a post
  const likePost = async (postAccount) => {
    try {
      // Liking post
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  // Repost a post (placeholder - not implemented in smart contract yet)
  const repostPost = async (postAccount) => {
    try {
      // Reposting
    } catch (error) {
      console.error('Error reposting:', error);
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'now';
      if (diffMins < 60) return `${diffMins}m`;
      if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h`;
      return `${Math.floor(diffMins / 1440)}d`;
    } catch {
      return 'unknown';
    }
  };

  // Format wallet address
  const formatAddress = (address) => {
    return formatAddressForDisplay(address);
  };

  // Only show main content when wallet is actively connected
  // Show main content if we have wallet address OR if onboarding is completed
  // But hide if wallet is explicitly disconnected
  const { isConnected: walletIsConnected, address: onboardingAddress } = useOnboarding();
  const hasWalletConnection = walletAddress || onboardingAddress;
  
  // Show connect screen only if no wallet address AND not connected
  if (!hasWalletConnection || (walletIsConnected === false && !walletAddress)) {
    return (
      <div className="main-content">
        <div className="content-header">
          <h1>Welcome to Znoospace</h1>
        </div>
        <div className="connect-prompt-main">
          <div className="connect-card">
            <h2>🚀 Connect Your Wallet</h2>
            <p>Connect your Solana wallet to start beaming your thoughts to the cosmos!</p>
            <div className="connect-instructions">
              <p>Use the sidebar to connect your Phantom or Solflare wallet and join the cosmic social revolution.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      {/* Welcome Message for Returning Users */}
      {showWelcome && welcomeDisplayName && (
        <WelcomeMessage 
          displayName={welcomeDisplayName}
          onDismiss={dismissWelcome}
          autoHide={true}
        />
      )}
      
      {/* Twitter-style Header with Tabs */}
      <div className="content-header-tabs">
        <div className="tab-container">
          <button 
            className={`tab-btn ${activeTab === 'foryou' ? 'active' : ''}`}
            onClick={() => setActiveTab('foryou')}
          >
            For you
          </button>
          <button 
            className={`tab-btn ${activeTab === 'following' ? 'active' : ''}`}
            onClick={() => setActiveTab('following')}
          >
            Following
          </button>
          <button 
            className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
        </div>
      </div>

      {/* Top Followed Users (Following tab) */}
      {activeTab === 'following' && topFollowedUsers.length > 0 && (
        <div className="following-highlights">
          <h4 className="following-highlights-title">✨ Your top follows</h4>
          {topFollowedUsers.map((user, idx) => (
            <div key={user.author || idx} className="following-highlight-card">
              <div 
                className="following-highlight-avatar"
                style={{ background: generateGradientFromSeed(user.author || 'default') }}
              />
              <div className="following-highlight-info">
                <span className="following-highlight-name">
                  @{user.displayName || user.username || user.author?.substring(0, 8)}
                </span>
                <span className="following-highlight-time">
                  · {formatTimestamp(user.timestamp)}
                </span>
                <p className="following-highlight-snippet">
                  {(user.content || '').replace(/https?:\/\/[^\s]+/g, '').trim().substring(0, 80)}
                  {(user.content || '').length > 80 ? '...' : ''}
                </p>
                {user.mediaUrl && (
                  <span className="following-highlight-media">📷 Media attached</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search Component */}
      {showSearch && (
        <SearchComponent 
          onSearchResults={handleSearchResults}
          initialQuery={searchQuery}
          initialType={searchType}
        />
      )}

      {/* Twitter-style Post Composer */}
      <div className="post-composer">
        <form onSubmit={createBeam} className="composer-form">
          <div className="composer-header">
            <div className="composer-avatar">
              <div 
                className="avatar-circle"
                style={{
                  background: generateGradientFromSeed(walletAddress || 'default')
                }}
              >
                {(() => {
                  // Use the EXACT same logic as ProfilePage for consistency
                  const profileToUse = completeProfile || profile || {};
                  
                  // Profile avatar logic - using same logic as ProfilePage for consistency
                  
                  // Match ProfilePage's exact display logic
                  if (profileToUse.profileImage && profileToUse.profileImage.trim() !== '') {
                    return (
                      <img 
                        src={profileToUse.profileImage} 
                        alt="Profile"
                        className="composer-avatar-image user-uploaded"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const parent = e.target.parentElement;
                          if (parent && !parent.querySelector('.composer-avatar-placeholder')) {
                            const fallbackDiv = document.createElement('div');
                            fallbackDiv.className = 'composer-avatar-placeholder';
                            fallbackDiv.innerHTML = '<svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>';
                            parent.appendChild(fallbackDiv);
                          }
                        }}
                        onLoad={() => {
                          // Profile image loaded successfully
                        }}
                      />
                    );
                  } else {
                    // Use the same placeholder as ProfilePage
                    return (
                      <div className="composer-avatar-placeholder">
                        <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                        </svg>
                      </div>
                    );
                  }
                })()} 
              </div>
            </div>
            <div className="composer-content">
              <textarea
                value={newBeamContent}
                onChange={(e) => {
                  const maxLength = getCharacterLimit(tokenBalance);
                  if (e.target.value.length <= maxLength) {
                    setNewBeamContent(e.target.value);
                  }
                }}
                placeholder={
                  profile?.displayName 
                    ? `What's on your mind, ${profile.displayName}?`
                    : profile?.username 
                    ? `What's on your mind, ${profile.username}?`
                    : "What's happening?"
                }
                className="composer-textarea"
                disabled={beamsLoading}
                maxLength={getCharacterLimit(tokenBalance)}
              />
            </div>
          </div>
          
          {/* Media Upload Section */}
          {showMediaUpload && (
            <div className="media-upload-section">
              <MediaUpload
                onMediaUploaded={handleMediaUploaded}
                onUploadStart={handleMediaUploadStart}
                onUploadError={handleMediaUploadError}
                walletAddress={walletAddress}
                disabled={beamsLoading || mediaUploading}
                maxFiles={1}
                showPreview={true}
              />
            </div>
          )}

          {/* Show uploaded media preview */}
          {uploadedMedia.length > 0 && (
            <div className="uploaded-media-preview">
              <div className="media-preview-header">
                <span>� Media uploaded to IPFS:</span>
                <button 
                  type="button" 
                  className="clear-media-btn"
                  onClick={clearUploadedMedia}
                  disabled={beamsLoading}
                >
                  ✕ Remove
                </button>
              </div>
              {uploadedMedia.map((media, index) => (
                <div key={index} className="media-preview-item">
                  <a 
                    href={media.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="media-link"
                    title="Stored on IPFS via Pinata"
                  >
                    � {media.fileName} ({(media.fileSize / 1024 / 1024).toFixed(2)} MB)
                  </a>
                </div>
              ))}
            </div>
          )}

          {/* Twitter-style Bottom Toolbar */}
          <div className="composer-toolbar">
            <div className="toolbar-icons">
              <button 
                type="button" 
                className={`toolbar-btn ${showMediaUpload ? 'active' : ''}`}
                title="Add media to IPFS"
                onClick={() => setShowMediaUpload(!showMediaUpload)}
                disabled={beamsLoading || mediaUploading}
              >
                {mediaUploading ? '⏳' : '🖼️'}
              </button>
              <button 
                type="button" 
                className={`toolbar-btn ${showMediaUpload ? 'active' : ''}`}
                title="Add GIF"
                onClick={() => setShowMediaUpload(!showMediaUpload)}
                disabled={beamsLoading || mediaUploading}
              >
                GIF
              </button>
              <button 
                type="button" 
                className="toolbar-btn" 
                title="Create poll (coming soon)"
                onClick={() => alert('📋 Polls are coming soon!')}
              >
                📋
              </button>
              <button 
                type="button" 
                className={`toolbar-btn ${showEmojiPicker ? 'active' : ''}`}
                title="Add emoji"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              >
                🙂
              </button>
              <button 
                type="button" 
                className="toolbar-btn" 
                title="Schedule post (coming soon)"
                onClick={() => alert('📅 Scheduled posts are coming soon!')}
              >
                📅
              </button>
              <button 
                type="button" 
                className="toolbar-btn" 
                title="Add location (coming soon)"
                onClick={() => alert('📍 Location tagging is coming soon!')}
              >
                📍
              </button>
              {uploadedMedia.length > 0 && (
                <span 
                  className="arweave-indicator" 
                  title="Media stored on IPFS via Pinata"
                >
                  � IPFS
                </span>
              )}
            </div>
            
            <div className="toolbar-right">
              <div className="character-counter">
                {newBeamContent.length > 0 && (
                  <span className={newBeamContent.length > getCharacterLimit(tokenBalance) * 0.9 ? 'warning' : ''}>
                    {getCharacterLimit(tokenBalance) - newBeamContent.length}
                  </span>
                )}
              </div>
              <button 
                type="submit" 
                className="post-btn"
                disabled={(!newBeamContent.trim() && uploadedMedia.length === 0) || beamsLoading || mediaUploading}
              >
                {beamsLoading ? (
                  mediaUploading ? 'Uploading to IPFS...' : 'Posting...'
                ) : (
                  uploadedMedia.length > 0 ? 'Beam with Media' : 'Post'
                )}
              </button>
            </div>
          </div>

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div className="emoji-picker-panel">
              {['😀','😂','🥹','😍','🤩','😎','🥳','🤔','😤','😭','💀','👻','🔥','💯','❤️','💜','🚀','🌙','⭐','✨','👀','🙌','👏','💪','🎉','🎊','💎','🪙','📡','🛸','🌌','🌠','☄️','🌍','🧑‍🚀','👽','🤖','🐵','🦊','🐸','🌈','🍕','☕','🎵','📸','💬','📌','🔗','⚡','🏆'].map(emoji => (
              <button
                key={emoji}
                type="button"
                className="emoji-btn"
                onClick={() => {
                  setNewBeamContent(prev => prev + emoji);
                  setShowEmojiPicker(false);
                }}
              >
                {emoji}
              </button>
            ))}
            </div>
          )}
        </form>
      </div>

      {/* Feed */}
      <div className="beams-feed">
        {/* Search Results Summary */}
        {searchQuery && (
          <div className="search-results-header">
            <p>
              {beams.length === 0 
                ? `No beams found for "${searchQuery}"` 
                : `Showing ${beams.length} cosmic result${beams.length !== 1 ? 's' : ''} for "${searchQuery}"`
              }
            </p>
            <button 
              className="clear-search-btn-main"
              onClick={() => {
                setSearchQuery('');
                refreshBeams();
              }}
            >
              Clear search
            </button>
          </div>
        )}
        
        {/* Blocked Users Management Panel */}
        {showBlockedUsers && (
          <div className="blocked-users-panel">
            <div className="blocked-users-header">
              <h3>🚫 Blocked Users</h3>
              <button className="close-blocked-btn" onClick={() => setShowBlockedUsers(false)}>✕</button>
            </div>
            {(() => {
              const blockedList = solanaService?.getBlockedUsers ? solanaService.getBlockedUsers() : [];
              if (blockedList.length === 0) {
                return <p className="no-blocked-msg">You haven't blocked anyone yet.</p>;
              }
              return (
                <div className="blocked-users-list">
                  {blockedList.map(addr => (
                    <div key={addr} className="blocked-user-item">
                      <span className="blocked-user-addr">@{addr.slice(0, 8)}...{addr.slice(-4)}</span>
                      <button
                        className="unblock-btn"
                        onClick={async () => {
                          await solanaService.unblockUser(addr);
                          setBlockVersion(v => v + 1);
                        }}
                      >
                        Unblock
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Tab-specific loading states */}
        {activeTab === 'profile' && profileLoading ? (
          <div className="loading-beams">
            <div className="cosmic-loader">
              <div className="loading-animation">
                <div className="cosmic-spinner"></div>
                <div className="loading-dots">
                  <span className="dot">👤</span>
                  <span className="dot">🔍</span>
                  <span className="dot">✨</span>
                </div>
              </div>
              <p>Loading your posts from the blockchain...</p>
            </div>
          </div>
        ) : activeTab === 'foryou' && forYouLoading && forYouBeams.length === 0 ? (
          <div className="loading-beams">
            <div className="cosmic-loader">
              <div className="loading-animation">
                <div className="cosmic-spinner"></div>
                <div className="loading-dots">
                  <span className="dot">🌐</span>
                  <span className="dot">🔍</span>
                  <span className="dot">✨</span>
                </div>
              </div>
              <p>Discovering posts from the Znoospace community...</p>
            </div>
          </div>
        ) : activeTab === 'following' && followingLoading ? (
          <div className="loading-beams">
            <div className="cosmic-loader">
              <div className="loading-animation">
                <div className="cosmic-spinner"></div>
                <div className="loading-dots">
                  <span className="dot">👥</span>
                  <span className="dot">🔍</span>
                  <span className="dot">✨</span>
                </div>
              </div>
              <p>Loading posts from people you follow...</p>
            </div>
          </div>
        ) : (beamsLoading) && beams.length === 0 && activeTab !== 'foryou' ? (
          <div className="loading-beams">
            <div className="cosmic-loader">
              <div className="loading-animation">
                <div className="cosmic-spinner"></div>
                <div className="loading-dots">
                  <span className="dot">✨</span>
                  <span className="dot">🌌</span>
                  <span className="dot">🚀</span>
                </div>
              </div>
              <h3>🔍 Scanning the cosmos for your beams...</h3>
              <p>Searching the blockchain for your existing posts</p>
            </div>
          </div>
        ) : displayBeams.length === 0 && activeTab === 'profile' && !profileLoading ? (
          <div className="no-beams">
            <h3>👤 No posts yet</h3>
            <p>Your posts will appear here. Write your first beam!</p>
            <button 
              className="tab-switch-btn"
              onClick={() => setActiveTab('foryou')}
            >
              Browse the feed
            </button>
          </div>
        ) : displayBeams.length === 0 && activeTab === 'following' ? (
          <div className="no-beams">
            <h3>👥 No followed users yet</h3>
            <p>Follow other users from their beams to see their posts here.</p>
            <button 
              className="tab-switch-btn"
              onClick={() => setActiveTab('foryou')}
            >
              Browse all beams
            </button>
          </div>
        ) : displayBeams.length === 0 && activeTab === 'foryou' && !forYouLoading ? (
          <div className="no-beams">
            <h3>🌐 No posts found yet</h3>
            <p>Be the first to post! Your beam will appear here and in the global feed.</p>
          </div>
        ) : displayBeams.length === 0 && !searchQuery ? (
          <div className="no-beams">
            <h3>🌌 No beams in the cosmos yet</h3>
            <p>When you beam something, it will show up here in the cosmic feed.</p>
          </div>
        ) : displayBeams.length === 0 && searchQuery ? (
          <div className="no-beams">
            <h3>🔍 No beams found in the cosmos</h3>
            <p>Try searching with different keywords or hashtags.</p>
          </div>
        ) : (
          displayBeams.slice(0, renderCount).map((beam) => {
            return (
              <div key={beam.id} className="beam beam-card">
                <div className="beam-avatar">
                  {(() => {
                    // Create a profile object for the beam author
                    const beamAuthorProfile = {
                      profileImage: beam.profileImage || beam.avatarUrl,
                      username: beam.username,
                      displayName: beam.displayName
                    };
                    
                    const avatar = getBestAvatar(beamAuthorProfile, beam.author);
                    
                    if (avatar.type === 'image' || avatar.type === 'generated') {
                      return (
                        <img 
                          src={avatar.src} 
                          alt={avatar.alt}
                          className={`beam-avatar-image ${avatar.type === 'generated' ? 'generated-avatar' : ''}`}
                          onError={(e) => {
                            // Fallback to emoji if image fails
                            e.target.style.display = 'none';
                            const parent = e.target.parentElement;
                            if (parent && !parent.querySelector('.beam-avatar-emoji')) {
                              const fallbackSpan = document.createElement('span');
                              fallbackSpan.className = 'beam-avatar-emoji';
                              fallbackSpan.textContent = '🚀';
                              parent.appendChild(fallbackSpan);
                            }
                          }}
                        />
                      );
                    } else {
                      return <span className="beam-avatar-emoji">🚀</span>;
                    }
                   })()}
                </div>
                <div className="beam-content">
                  <div className="beam-header">
                    <div className="beam-author">
                      <span className="author-name">
                        {(() => {
                          // Debug logging to see what data we have
                          // Beam author data available
                          
                          if (beam.author) {
                            const walletPrefix = beam.author.substring(0, 8);
                            
                            // Use EXACT same logic as "What's on your mind" placeholder
                            let userHandle = profile?.displayName || profile?.username;
                            
                            // Debug what we have
                            // Username selection logic applied
                            
                            // Only use beam data if we don't have profile data AND beam data is clean
                            if (!userHandle) {
                              const beamHandle = beam.username || beam.displayName;
                              if (beamHandle && !beamHandle.startsWith('user_') && !beamHandle.startsWith('User ') && !beamHandle.includes('...')) {
                                userHandle = beamHandle;
                              }
                            }
                            
                            // Final fallback to wallet prefix
                            if (!userHandle) {
                              userHandle = walletPrefix;
                            }
                            
                            return `@${userHandle}`;
                          } else {
                            return beam.displayName || beam.username || 'Cosmic Explorer';
                          }
                        })()}
                      </span>
                      <span className="beam-time">· {formatTimestamp(beam.timestamp)}</span>
                      {/* Post action menu — follow/block for other users */}
                      {solanaService?.isFollowing && beam.author && beam.author !== walletAddress && (
                        <div className="post-menu-container">
                          <button 
                            className="post-menu-trigger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPostMenuOpen(postMenuOpen === beam.id ? null : beam.id);
                            }}
                          >
                            ···
                          </button>
                          {postMenuOpen === beam.id && (
                            <div className="post-menu-dropdown">
                              <button
                                className={`post-menu-item ${solanaService.isFollowing(beam.author)?.isFollowing ? 'following' : ''}`}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const status = solanaService.isFollowing(beam.author);
                                  if (status?.isFollowing) {
                                    await solanaService.unfollowUser(beam.author);
                                  } else {
                                    await solanaService.followUser(beam.author);
                                  }
                                  setFollowVersion(v => v + 1);
                                  setPostMenuOpen(null);
                                }}
                              >
                                {solanaService.isFollowing(beam.author)?.isFollowing ? '✓ Following' : '+ Follow'} @{beam.author?.slice(0, 8)}
                              </button>
                              <button
                                className="post-menu-item block-item"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Block @${beam.author?.slice(0, 8)}...? Their posts will be hidden from your feed.`)) {
                                    await solanaService.blockUser(beam.author);
                                    setBlockVersion(v => v + 1);
                                  }
                                  setPostMenuOpen(null);
                                }}
                              >
                                🚫 Block @{beam.author?.slice(0, 8)}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="beam-text">
                    <div>
                      {(() => {
                        // Clean the content first to remove any existing Solana Explorer text
                        let cleanContent = (beam.content || '')
                          .replace(/🔗\s*View on Solana Explorer:?\s*/gi, '')
                          .replace(/View on Solana Explorer:?\s*/gi, '')
                          .replace(/https?:\/\/explorer\.solana\.com\/[^\s]*/gi, '')
                          .trim();
                        
                        return parseTagsAndMentions(cleanContent).filter(part => {
                          // Filter out any remaining Solana Explorer links
                          return !(part.type === 'url' && part.linkType === 'solana_explorer');
                        }).map((part, index) => {
                          if (part.type === 'hashtag') {
                            return (
                              <span key={index} className="hashtag" data-tag={part.tag}>
                                {part.content}
                              </span>
                            );
                          } else if (part.type === 'mention') {
                            return (
                              <span key={index} className="mention" data-mention={part.mention}>
                                {part.content}
                              </span>
                            );
                          } else if (part.type === 'url') {
                            // Skip ALL Solana Explorer links to avoid duplication
                            if (part.linkType === 'solana_explorer') {
                              return null; // Don't render Solana Explorer links in content
                            }
                            return (
                              <a 
                                key={index} 
                                href={part.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="beam-link external-link"
                              >
                                {part.content}
                              </a>
                            );
                          } else {
                            return <span key={index}>{part.content}</span>;
                          }
                        });
                      })()}
                    </div>
                  </div>
                  
                  {/* Show media if available (images + video) */}
                  {beam.mediaUrl && (
                    <div className="beam-media">
                      {beam.mediaUrl.match(/\.(mp4|webm|mov|ogg)$/i) || beam.mediaType === 'video' ? (
                        <video 
                          src={beam.mediaUrl} 
                          controls 
                          className="beam-video"
                          preload="metadata"
                          playsInline
                        />
                      ) : (
                        <div className="beam-image-wrapper">
                          <div className="beam-image-shimmer" />
                          <img 
                            src={beam.mediaUrl} 
                            alt="Beam media" 
                            className="beam-image"
                            loading="lazy"
                            onLoad={(e) => { 
                              const shimmer = e.target.previousElementSibling;
                              if (shimmer) shimmer.style.display = 'none';
                              e.target.style.opacity = '1';
                            }}
                            onError={(e) => { 
                              const shimmer = e.target.previousElementSibling;
                              if (shimmer) shimmer.style.display = 'none';
                              e.target.style.display = 'none'; 
                            }}
                            style={{ opacity: 0, transition: 'opacity 0.3s ease' }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Post metadata: URL, display name, and blockchain link */}
                  <div className="beam-metadata">
                    {/* Show URL if it's a URL post */}
                    {beam.url && (
                      <div className="beam-url-info">
                        <span className="url-label">🔗 Link:</span>
                        <a href={beam.url} target="_blank" rel="noopener noreferrer" className="beam-url">
                          {beam.url}
                        </a>
                      </div>
                    )}
                    

                    
                    {/* Blockchain transaction link */}
                    {!beam.id.startsWith('fallback_') && (
                      <div className="beam-blockchain-link">
                        <a 
                          href={`https://explorer.solana.com/tx/${beam.id}?cluster=devnet`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="solana-explorer-link"
                        >
                          🔗 View on Solana Explorer
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {/* Show hashtags for this beam */}
                  {(() => {
                    const tags = extractHashtags(beam.content || '');
                    return tags.length > 0 ? (
                    <div className="beam-hashtags">
                      {tags.map((tag, index) => (
                        <span 
                          key={index} 
                          className="beam-hashtag-tag"
                          onClick={() => {
                            setSearchQuery(`#${tag}`);
                            setShowSearch(true);
                            handleSearchResults(beams.filter(b => 
                              extractHashtags(b.content || '').includes(tag)
                            ), `#${tag}`, 'hashtag');
                          }}
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  ) : null;
                  })()}
                  <div className="beam-actions">
                    <button className="action-btn comment">
                      💬
                      <span>0</span>
                    </button>
                    <button className="action-btn signal-boost">
                      📡
                      <span>0</span>
                    </button>
                    <button className="action-btn cosmic-like">
                      ✨
                      <span>{beam.likes || 0}</span>
                    </button>
                    <button className="action-btn share">
                      🌌
                    </button>
                    <button 
                      className="action-btn flag-btn"
                      onClick={() => handleFlagContent(beam.id)}
                      title="Flag inappropriate content"
                    >
                      🚩
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* Progressive rendering sentinel — triggers loading more beam DOM nodes as user scrolls */}
        {displayBeams.length > renderCount && (
          <div ref={renderSentinelRef} style={{ height: 1 }} />
        )}
        
        {/* Pagination Controls */}
        {!searchQuery && (
          <div className="pagination-controls">
            {loading && (
              <div className="loading-indicator">
                <span>📡 Scanning the cosmos...</span>
              </div>
            )}
            
            {/* For You tab: Load More from global feed */}
            {activeTab === 'foryou' && forYouHasMore && !forYouLoading && (
              <button 
                className="load-more-btn"
                onClick={async () => {
                  if (!solanaService?.getForYouFeed || forYouLoading) return;
                  setForYouLoading(true);
                  try {
                    const result = await solanaService.getForYouFeed(30, forYouCursor);
                    if (result.success && result.beams) {
                      setForYouBeams(prev => {
                        const existingSigs = new Set(prev.map(b => b.id || b.signature));
                        const newBeams = result.beams.filter(b => !existingSigs.has(b.id || b.signature));
                        return [...prev, ...newBeams];
                      });
                      setForYouHasMore(result.hasMore || false);
                      setForYouCursor(result.nextCursor || null);
                    }
                  } catch (err) {
                    console.error('❌ Failed to load more For You beams:', err);
                  } finally {
                    setForYouLoading(false);
                  }
                }}
                disabled={forYouLoading}
              >
                {forYouLoading ? '📡 Loading...' : '🌐 Load More Posts'}
              </button>
            )}

            {/* Other tabs: existing pagination */}
            {activeTab !== 'foryou' && hasMoreBeams && !loading && (
              <button 
                className="load-more-btn"
                onClick={loadMoreBeams}
                disabled={loading}
              >
                🌌 Load More Beams from the Cosmos
              </button>
            )}
            
            {displayBeams.length > 0 && !forYouHasMore && !hasMoreBeams && (
              <div className="end-of-cosmos">
                <span>✨ You've reached the edge of the cosmos! ✨</span>
              </div>
            )}

            {/* Manage Blocked Users link */}
            {solanaService?.getBlockedUsers && (
              <button 
                className="manage-blocked-btn"
                onClick={() => setShowBlockedUsers(!showBlockedUsers)}
              >
                🚫 {showBlockedUsers ? 'Hide' : 'Manage'} Blocked Users
                {(() => {
                  const count = solanaService.getBlockedUsers().length;
                  return count > 0 ? ` (${count})` : '';
                })()}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Content Flagging Modal */}
      {showFlagging && flaggingContentId && (
        <ContentFlagging
          contentId={flaggingContentId}
          contentType="POST"
          onFlagSubmitted={handleFlagSubmitted}
          onClose={handleCloseFlagging}
        />
      )}

      {/* Stake Requirements Modal */}
      {showStakeModal && (
        <StakeModal
          requiredStake={requiredStake}
          onClose={handleCloseStakeModal}
          onGetTokens={handleGetTokens}
        />
      )}


    </div>
  );
};

export default MainContent;
